import React, { useMemo } from 'react';
import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Card, Empty, Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import StatisticsProvider from '../../StatisticsProvider';
import useStatistics from '../../../../hooks/data/useStatistics';
import useServiceProvider from '../../../../hooks/data/useServiceProvider';
import theme from '../../../../styles/Theme';
import HelperMethods from '../../../../services/exports/HelperMethods';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

function ConversionChart({ steps = [], color }) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'Views:Dashboard:Misc:OrderConversion',
  });

  const labels = useMemo(() => steps.map((item) => t(`events.${item.type}`, item.type)), [steps, t]);

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: t('users', 'Users'),
          data: steps.map((item) => item.conversion_value),
          backgroundColor: color,
          borderRadius: 6,
          maxBarThickness: 56,
        },
      ],
    }),
    [labels, steps, color, t],
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label(context) {
              const step = steps[context.dataIndex];

              if (!step) {
                return `${context.raw}`;
              }

              return [
                `${t('users', 'Users')}: ${step.conversion_value}`,
                `Conversion: ${HelperMethods.formatPercentage(step.conversion_percentage ?? 0)}`,
                `Step conversion: ${HelperMethods.formatPercentage(step.dropoff_percentage ?? 0)}`,
                `Dropoff: ${step.dropoff_value ?? 0}`,
              ];
            },
          },
        },
        datalabels: {
          anchor: 'end',
          align: 'end',
          offset: 4,
          clamp: true,
          color: theme.color.text?.primary || '#1f1f1f',
          backgroundColor: theme.color.background?.inkWhite?.white_1 || '#fff',
          borderColor: theme.color.brand?.inkGrey?.grey_1 || '#d9d9d9',
          borderWidth: 1,
          borderRadius: 4,
          padding: 4,
          formatter(value, context) {
            const step = steps[context.dataIndex];

            if (!step) {
              return value;
            }

            return `${value} (${HelperMethods.formatPercentage(step.conversion_percentage ?? 0)})`;
          },
        },
      },
      scales: {
        x: {
          ticks: {
            autoSkip: false,
          },
          grid: {
            display: false,
          },
        },
        y: {
          grace: '15%',
          beginAtZero: true,
          ticks: {
            precision: 0,
          },
        },
      },
    }),
    [steps, t],
  );

  return (
    <div style={{ height: 320 }}>
      <Bar data={chartData} options={chartOptions} />
    </div>
  );
}

function Metric({ title, data, loading, color, className }) {
  const steps = data ?? [];

  if (loading) {
    return (
      <Card title={title} className={className}>
        <Skeleton active />
      </Card>
    );
  }

  if (!steps.length) {
    return (
      <Card title={title} className={className}>
        <Empty className="my-auto" />
      </Card>
    );
  }

  return (
    <Card title={title} className={className}>
      <ConversionChart steps={steps} color={color} />
    </Card>
  );
}

function Metrics() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'Views:Dashboard:Misc:OrderConversion',
  });
  const { stats, loadingMain } = useStatistics();
  const { hasExternalDelivery } = useServiceProvider();

  if (!stats) {
    return null;
  }

  return (
    <div className="space-y-lg">
      <Metric
        title={t('metrics.pickup')}
        data={stats.order_conversion_pickup}
        loading={loadingMain}
        color={theme.color.brand.primary}
      />
      <Metric
        title={t('metrics.internal_delivery')}
        data={stats.order_conversion_internal_delivery}
        loading={loadingMain}
        color="#0388fc"
      />
      {hasExternalDelivery && (
        <Metric
          title={t('metrics.external_delivery')}
          data={stats.order_conversion_external_delivery}
          loading={loadingMain}
          color="#fc5e03"
        />
      )}
    </div>
  );
}

export default function OrderConversion({ dateRangeFilter, renderDateRangeFilter }) {
  return (
    <StatisticsProvider
      dateRangeFilter={dateRangeFilter}
      mainMetrics={[
        'order_conversion_pickup',
        'order_conversion_internal_delivery',
        'order_conversion_external_delivery',
      ]}
    >
      {renderDateRangeFilter && renderDateRangeFilter()}
      <Metrics />
    </StatisticsProvider>
  );
}
